import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  Shield, 
  Search, 
  RefreshCw, 
  Edit2, 
  Trash2, 
  Plus, 
  Eye,
  LogIn,
  LogOut,
  FileText,
  User,
  Clock,
  Filter,
  Download,
  AlertTriangle,
  CheckCircle
} from 'lucide-react';
import { useToast } from '../hooks/use-toast';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AuditLogs = () => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [logs, setLogs] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loginHistory, setLoginHistory] = useState([]);
  const [filters, setFilters] = useState({
    entity_type: '',
    action: '',
    search: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      const [logsRes, summaryRes, loginRes] = await Promise.all([
        axios.get(`${API_URL}/api/audit-logs?limit=200`, { headers }),
        axios.get(`${API_URL}/api/audit-logs/summary`, { headers }),
        axios.get(`${API_URL}/api/audit-logs/login-history`, { headers })
      ]);

      setLogs(logsRes.data || []);
      setSummary(summaryRes.data);
      setLoginHistory(loginRes.data || []);
    } catch (error) {
      console.error('Error fetching audit logs:', error);
      toast({
        title: "Error",
        description: "Failed to load audit logs",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchFilteredLogs = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams();
      if (filters.entity_type) params.append('entity_type', filters.entity_type);
      if (filters.action) params.append('action', filters.action);
      params.append('limit', '200');

      const response = await axios.get(`${API_URL}/api/audit-logs?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLogs(response.data || []);
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to filter logs",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const getActionIcon = (action) => {
    switch (action) {
      case 'create': return <Plus className="w-4 h-4 text-green-500" />;
      case 'update': return <Edit2 className="w-4 h-4 text-blue-500" />;
      case 'delete': return <Trash2 className="w-4 h-4 text-red-500" />;
      case 'login': return <LogIn className="w-4 h-4 text-green-500" />;
      case 'logout': return <LogOut className="w-4 h-4 text-gray-500" />;
      case 'view': return <Eye className="w-4 h-4 text-purple-500" />;
      case 'status_change': return <RefreshCw className="w-4 h-4 text-orange-500" />;
      case 'export': return <Download className="w-4 h-4 text-indigo-500" />;
      default: return <FileText className="w-4 h-4 text-gray-500" />;
    }
  };

  const getActionBadge = (action) => {
    const colors = {
      create: 'bg-green-100 text-green-800',
      update: 'bg-blue-100 text-blue-800',
      delete: 'bg-red-100 text-red-800',
      login: 'bg-green-100 text-green-800',
      logout: 'bg-gray-100 text-gray-800',
      view: 'bg-purple-100 text-purple-800',
      status_change: 'bg-orange-100 text-orange-800',
      export: 'bg-indigo-100 text-indigo-800'
    };
    return colors[action] || 'bg-gray-100 text-gray-800';
  };

  const filteredLogs = logs.filter(log => {
    if (filters.search) {
      const search = filters.search.toLowerCase();
      return (
        log.entity_name?.toLowerCase().includes(search) ||
        log.user_name?.toLowerCase().includes(search) ||
        log.details?.toLowerCase().includes(search)
      );
    }
    return true;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <RefreshCw className="w-8 h-8 animate-spin text-blue-500" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Shield className="w-6 h-6 text-blue-600" />
            Audit Logs & Legal Protection
          </h1>
          <p className="text-gray-500">Immutable activity logs for professional risk protection</p>
        </div>
        <Button variant="outline" onClick={fetchData}>
          <RefreshCw className="w-4 h-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-gray-500">Total Logs</p>
                <p className="text-2xl font-bold">{summary?.total_logs || 0}</p>
              </div>
              <FileText className="w-8 h-8 text-blue-500" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-gray-500">Today's Activity</p>
                <p className="text-2xl font-bold text-green-600">{summary?.logs_today || 0}</p>
              </div>
              <Clock className="w-8 h-8 text-green-500" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-gray-500">This Week</p>
                <p className="text-2xl font-bold">{summary?.logs_this_week || 0}</p>
              </div>
              <Clock className="w-8 h-8 text-purple-500" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-gray-500">Login Sessions</p>
                <p className="text-2xl font-bold">{loginHistory.length}</p>
              </div>
              <LogIn className="w-8 h-8 text-indigo-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Activity by Type */}
      {summary?.by_action && Object.keys(summary.by_action).length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-sm">Activity Breakdown</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              {Object.entries(summary.by_action).map(([action, count]) => (
                <Badge key={action} variant="outline" className={getActionBadge(action)}>
                  {getActionIcon(action)}
                  <span className="ml-1 capitalize">{action}: {count}</span>
                </Badge>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      <Tabs defaultValue="all">
        <TabsList>
          <TabsTrigger value="all">
            <FileText className="w-4 h-4 mr-2" />
            All Activity
          </TabsTrigger>
          <TabsTrigger value="changes">
            <Edit2 className="w-4 h-4 mr-2" />
            Data Changes
          </TabsTrigger>
          <TabsTrigger value="logins">
            <LogIn className="w-4 h-4 mr-2" />
            Login History
          </TabsTrigger>
        </TabsList>

        {/* All Activity Tab */}
        <TabsContent value="all" className="space-y-4">
          {/* Filters */}
          <Card>
            <CardContent className="pt-4">
              <div className="flex gap-4 items-end">
                <div className="flex-1">
                  <label className="text-sm text-gray-500">Search</label>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <Input
                      placeholder="Search by name, user, or details..."
                      value={filters.search}
                      onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                      className="pl-10"
                    />
                  </div>
                </div>
                <div className="w-40">
                  <label className="text-sm text-gray-500">Entity Type</label>
                  <Select
                    value={filters.entity_type || "all"}
                    onValueChange={(value) => setFilters({ ...filters, entity_type: value === "all" ? "" : value })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="All types" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All types</SelectItem>
                      <SelectItem value="invoice">Invoice</SelectItem>
                      <SelectItem value="receipt">Receipt</SelectItem>
                      <SelectItem value="task">Task</SelectItem>
                      <SelectItem value="client">Client</SelectItem>
                      <SelectItem value="staff">Staff</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="w-40">
                  <label className="text-sm text-gray-500">Action</label>
                  <Select
                    value={filters.action || "all"}
                    onValueChange={(value) => setFilters({ ...filters, action: value === "all" ? "" : value })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="All actions" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All actions</SelectItem>
                      <SelectItem value="create">Create</SelectItem>
                      <SelectItem value="update">Update</SelectItem>
                      <SelectItem value="delete">Delete</SelectItem>
                      <SelectItem value="status_change">Status Change</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <Button onClick={fetchFilteredLogs}>
                  <Filter className="w-4 h-4 mr-2" />
                  Apply
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Logs List */}
          <Card>
            <CardHeader>
              <CardTitle>Activity Log</CardTitle>
              <CardDescription>Complete audit trail of all actions</CardDescription>
            </CardHeader>
            <CardContent>
              {filteredLogs.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <Shield className="w-12 h-12 mx-auto mb-4 text-gray-400" />
                  <p>No audit logs found</p>
                  <p className="text-sm">Activity will be recorded as you use the system</p>
                </div>
              ) : (
                <div className="space-y-3 max-h-[600px] overflow-y-auto">
                  {filteredLogs.map((log, idx) => (
                    <div key={idx} className="border rounded-lg p-4 hover:bg-gray-50">
                      <div className="flex items-start justify-between">
                        <div className="flex items-start gap-3">
                          <div className="mt-1">{getActionIcon(log.action)}</div>
                          <div>
                            <div className="flex items-center gap-2 mb-1">
                              <Badge className={getActionBadge(log.action)}>
                                {log.action}
                              </Badge>
                              <Badge variant="outline">{log.entity_type}</Badge>
                              {log.entity_name && (
                                <span className="font-medium">{log.entity_name}</span>
                              )}
                            </div>
                            {log.details && (
                              <p className="text-sm text-gray-600">{log.details}</p>
                            )}
                            <div className="flex items-center gap-4 mt-2 text-xs text-gray-500">
                              <span className="flex items-center gap-1">
                                <User className="w-3 h-3" />
                                {log.user_name}
                              </span>
                              <span className="flex items-center gap-1">
                                <Clock className="w-3 h-3" />
                                {new Date(log.timestamp).toLocaleString()}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                      
                      {/* Show old/new values for updates */}
                      {log.action === 'update' && (log.old_value || log.new_value) && (
                        <div className="mt-3 pt-3 border-t text-xs">
                          <div className="grid grid-cols-2 gap-4">
                            {log.old_value && Object.keys(log.old_value).length > 0 && (
                              <div>
                                <span className="text-gray-500">Before:</span>
                                <pre className="bg-red-50 p-2 rounded mt-1 overflow-x-auto">
                                  {JSON.stringify(log.old_value, null, 2).substring(0, 200)}
                                </pre>
                              </div>
                            )}
                            {log.new_value && Object.keys(log.new_value).length > 0 && (
                              <div>
                                <span className="text-gray-500">After:</span>
                                <pre className="bg-green-50 p-2 rounded mt-1 overflow-x-auto">
                                  {JSON.stringify(log.new_value, null, 2).substring(0, 200)}
                                </pre>
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Data Changes Tab */}
        <TabsContent value="changes">
          <Card>
            <CardHeader>
              <CardTitle>Data Modifications</CardTitle>
              <CardDescription>Track all creates, updates, and deletes</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3 max-h-[600px] overflow-y-auto">
                {logs.filter(l => ['create', 'update', 'delete', 'status_change'].includes(l.action)).map((log, idx) => (
                  <div key={idx} className="border rounded-lg p-4">
                    <div className="flex items-center gap-3">
                      {getActionIcon(log.action)}
                      <div>
                        <div className="flex items-center gap-2">
                          <Badge className={getActionBadge(log.action)}>{log.action}</Badge>
                          <span className="font-medium">{log.entity_type}</span>
                          {log.entity_name && <span>- {log.entity_name}</span>}
                        </div>
                        <p className="text-sm text-gray-500">
                          by {log.user_name} at {new Date(log.timestamp).toLocaleString()}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Login History Tab */}
        <TabsContent value="logins">
          <Card>
            <CardHeader>
              <CardTitle>Login History</CardTitle>
              <CardDescription>Track all login and logout events</CardDescription>
            </CardHeader>
            <CardContent>
              {loginHistory.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <LogIn className="w-12 h-12 mx-auto mb-4 text-gray-400" />
                  <p>No login history recorded yet</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {loginHistory.map((log, idx) => (
                    <div key={idx} className="flex items-center justify-between border rounded-lg p-4">
                      <div className="flex items-center gap-3">
                        {log.action === 'login' ? (
                          <LogIn className="w-5 h-5 text-green-500" />
                        ) : (
                          <LogOut className="w-5 h-5 text-gray-500" />
                        )}
                        <div>
                          <p className="font-medium">{log.user_email}</p>
                          <p className="text-sm text-gray-500">
                            {log.ip_address || 'IP not recorded'}
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        <Badge className={log.action === 'login' ? 'bg-green-100 text-green-800' : 'bg-gray-100'}>
                          {log.action}
                        </Badge>
                        <p className="text-sm text-gray-500 mt-1">
                          {new Date(log.timestamp).toLocaleString()}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Legal Notice */}
      <Card className="border-blue-200 bg-blue-50">
        <CardContent className="pt-4">
          <div className="flex items-start gap-3">
            <Shield className="w-6 h-6 text-blue-600 mt-1" />
            <div>
              <h4 className="font-medium text-blue-900">Legal Protection Notice</h4>
              <p className="text-sm text-blue-700 mt-1">
                All audit logs are immutable and timestamped. They provide a complete trail of who made what changes and when. 
                This data can be used for compliance reporting, dispute resolution, and professional liability protection.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default AuditLogs;
